import { download, http } from './request'

/** 与后端 /biz/config/systemPanel 对齐 */
const PANEL_PREFIX = '/biz/config/systemPanel'

/** 顶部统计卡片（前端展示用字段名） */
export interface SystemConfigSummary {
  categoryCount: number
  itemCount: number
  modifyRecordCount: number
  completenessPercent: number
}

/** 后端 SystemConfigPanelStatsResult */
interface SystemConfigPanelStatsRaw {
  categoryCount?: number
  configItemCount?: number
  recentModifyCount?: number
  completenessPercent?: number
}

/** 基础配置表单（前端表单字段名） */
export interface SystemConfigPayload {
  systemName: string
  systemVersion: string
  systemDescription: string
  /** 已保存的 LOGO 访问地址 */
  logoUrl?: string | null
  supportPhone: string
  supportEmail: string
  managementAgency: string
  /** SOS：外交部领事保护中心 */
  sosMofaConsularCenterPhone: string
  /** SOS：驻外领事馆 */
  sosOverseasConsulatePhone: string
  /** SOS：贵州省外事办 */
  sosGuizhouForeignAffairsOfficePhone: string
  /** 客服中心电话 */
  customerServicePhone: string
  /** 客服群二维码 */
  customerServiceGroupQrUrl: string
  /** 融易达 AppId */
  smsRongyidaAppId: string
  /** 融易达 AppKey */
  smsRongyidaAppKey: string
  /** 融易达发送地址 */
  smsRongyidaSendUrl: string
  /** 短信签名（不含【】） */
  smsRongyidaSignName: string
  /** 短信总开关 true/false */
  smsEnabled: string
  /** 登录手机验证码开关 true/false */
  loginSmsOpen: string
  /** 登录完整性校验 true/false；仅后台政府账号（超管除外），且需 icsp 已启用 */
  integrityCheckOpen: string
  /** 高风险预警外事办电话 */
  smsWsbPhone: string
  /** 归国提醒链接 */
  smsReturnLinkUrl: string
  /** 案件审批超时天数 */
  smsCaseTimeoutDays: string
  /** VCS 是否启用 true/false */
  vcsEnabled: string
  /** VCS 门户根地址 */
  vcsBaseUrl: string
  /** VCS 企业代码 */
  vcsGroupId: string
  /** VCS 登录账号 */
  vcsUsername: string
  /** VCS 登录密码（详情不回显；留空保存表示不修改） */
  vcsPassword: string
  /** VCS 连接超时毫秒 */
  vcsConnectTimeoutMs: number
  /** VCS 读取超时毫秒 */
  vcsReadTimeoutMs: number
  /** 当前操作手册文件名（详情回显；保存配置时不提交） */
  operationManualFileName?: string
  /** 手册文件 ID，走 /dev/file/download?id= 鉴权下载 */
  operationManualFileId?: string
  /** 手册 MinIO 公网地址，下载方式与 LOGO 预览相同 */
  operationManualUrl?: string
  /** 手册最近上传时间 */
  operationManualUpdatedAt?: string
}
interface SystemConfigPanelDetailRaw {
  systemName?: string
  systemVersion?: string
  systemDescription?: string
  systemLogo?: string
  supportPhone?: string
  supportEmail?: string
  managementAgency?: string
  sosMofaConsularCenterPhone?: string
  sosOverseasConsulatePhone?: string
  sosGuizhouForeignAffairsOfficePhone?: string
  customerServicePhone?: string
  customerServiceGroupQrUrl?: string
  smsRongyidaAppId?: string
  smsRongyidaAppKey?: string
  smsRongyidaSendUrl?: string
  smsRongyidaSignName?: string
  smsEnabled?: string
  loginSmsOpen?: string
  integrityCheckOpen?: string
  smsWsbPhone?: string
  smsReturnLinkUrl?: string
  smsCaseTimeoutDays?: string
  vcsEnabled?: string
  vcsBaseUrl?: string
  vcsGroupId?: string
  vcsUsername?: string
  vcsPassword?: string
  vcsConnectTimeoutMs?: number
  vcsReadTimeoutMs?: number
  operationManualFileName?: string
  operationManualName?: string
  operationManualFileId?: string
  operationManualUrl?: string
  operationManualUpdatedAt?: string
  operationManualUpdateTime?: string
}

function num(v: unknown, def: number): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (!Number.isNaN(n)) return n
  }
  return def
}

function mapStats(raw: SystemConfigPanelStatsRaw): SystemConfigSummary {
  return {
    categoryCount: num(raw.categoryCount, 0),
    itemCount: num(raw.configItemCount, 0),
    modifyRecordCount: num(raw.recentModifyCount, 0),
    completenessPercent: num(raw.completenessPercent, 0),
  }
}

function mapDetailToPayload(raw: SystemConfigPanelDetailRaw): SystemConfigPayload {
  const logoTrim =
    raw.systemLogo != null && String(raw.systemLogo).trim() !== ''
      ? String(raw.systemLogo).trim()
      : ''
  return {
    systemName: raw.systemName ?? '',
    systemVersion: raw.systemVersion ?? '',
    systemDescription: raw.systemDescription ?? '',
    logoUrl: logoTrim || null,
    supportPhone: raw.supportPhone ?? '',
    supportEmail: raw.supportEmail ?? '',
    managementAgency: raw.managementAgency ?? '',
    sosMofaConsularCenterPhone: raw.sosMofaConsularCenterPhone ?? '',
    sosOverseasConsulatePhone: raw.sosOverseasConsulatePhone ?? '',
    sosGuizhouForeignAffairsOfficePhone: raw.sosGuizhouForeignAffairsOfficePhone ?? '',
    customerServicePhone: raw.customerServicePhone ?? '',
    customerServiceGroupQrUrl: raw.customerServiceGroupQrUrl ?? '',
    smsRongyidaAppId: raw.smsRongyidaAppId ?? '',
    smsRongyidaAppKey: raw.smsRongyidaAppKey ?? '',
    smsRongyidaSendUrl: raw.smsRongyidaSendUrl ?? 'http://58.42.9.213:9998/yktsms/send',
    smsRongyidaSignName: raw.smsRongyidaSignName ?? '贵州省外事办公室',
    smsEnabled: raw.smsEnabled === 'true' ? 'true' : 'false',
    loginSmsOpen: raw.loginSmsOpen === 'true' ? 'true' : 'false',
    integrityCheckOpen: raw.integrityCheckOpen === 'true' ? 'true' : 'false',
    smsWsbPhone: raw.smsWsbPhone ?? '',
    smsReturnLinkUrl: raw.smsReturnLinkUrl ?? '',
    smsCaseTimeoutDays: raw.smsCaseTimeoutDays ?? '1',
    vcsEnabled: raw.vcsEnabled === 'true' ? 'true' : 'false',
    vcsBaseUrl: raw.vcsBaseUrl ?? '',
    vcsGroupId: raw.vcsGroupId ?? '',
    vcsUsername: raw.vcsUsername ?? '',
    vcsPassword: '',
    vcsConnectTimeoutMs: num(raw.vcsConnectTimeoutMs, 10000),
    vcsReadTimeoutMs: num(raw.vcsReadTimeoutMs, 60000),
    operationManualFileName: String(
      raw.operationManualFileName ?? raw.operationManualName ?? '',
    ).trim(),
    operationManualFileId: String(raw.operationManualFileId ?? '').trim(),
    operationManualUrl: String(raw.operationManualUrl ?? '').trim(),
    operationManualUpdatedAt: String(
      raw.operationManualUpdatedAt ?? raw.operationManualUpdateTime ?? '',
    ).trim(),
  }
}

export async function fetchSystemConfigSummary(): Promise<SystemConfigSummary> {
  const raw = await http.get<SystemConfigPanelStatsRaw>(`${PANEL_PREFIX}/stats`)
  return mapStats(raw ?? {})
}

export async function fetchSystemConfig(): Promise<SystemConfigPayload> {
  const raw = await http.get<SystemConfigPanelDetailRaw>(`${PANEL_PREFIX}/detail`)
  return mapDetailToPayload(raw ?? {})
}

function dataUrlToFile(dataUrl: string, fallbackName: string): File {
  const comma = dataUrl.indexOf(',')
  const meta = comma >= 0 ? dataUrl.slice(0, comma) : ''
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl
  const mime = /data:([^;]+)/i.exec(meta)?.[1] || 'image/png'
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  const ext =
    mime.includes('jpeg') || mime.includes('jpg')
      ? 'jpg'
      : mime.includes('gif')
        ? 'gif'
        : mime.includes('webp')
          ? 'webp'
          : 'png'
  const name = fallbackName.replace(/\.\w+$/, `.${ext}`)
  return new File([bytes], name, { type: mime })
}

/** 库里若仍是 data URL，先传到文件服务，保存时只提交可访问地址 */
async function ensurePublicImageUrl(
  value: string | null | undefined,
  filename: string,
): Promise<string> {
  const s = String(value ?? '').trim()
  if (!s) return ''
  if (/^data:image\//i.test(s)) {
    return uploadSystemLogo(dataUrlToFile(s, filename))
  }
  return s
}

export async function saveSystemConfig(payload: SystemConfigPayload): Promise<{
  logoUrl: string
  customerServiceGroupQrUrl: string
}> {
  const systemLogo = await ensurePublicImageUrl(payload.logoUrl, 'system-logo.png')
  const customerServiceGroupQrUrl = await ensurePublicImageUrl(
    payload.customerServiceGroupQrUrl,
    'cs-group-qr.png',
  )
  await http.post<void>(`${PANEL_PREFIX}/save`, {
    systemName: payload.systemName,
    systemVersion: payload.systemVersion,
    systemDescription: payload.systemDescription,
    systemLogo,
    supportPhone: payload.supportPhone,
    supportEmail: payload.supportEmail,
    managementAgency: payload.managementAgency,
    sosMofaConsularCenterPhone: payload.sosMofaConsularCenterPhone,
    sosOverseasConsulatePhone: payload.sosOverseasConsulatePhone,
    sosGuizhouForeignAffairsOfficePhone: payload.sosGuizhouForeignAffairsOfficePhone,
    customerServicePhone: payload.customerServicePhone,
    customerServiceGroupQrUrl,
    smsRongyidaAppId: payload.smsRongyidaAppId,
    smsRongyidaAppKey: payload.smsRongyidaAppKey,
    smsRongyidaSendUrl: payload.smsRongyidaSendUrl,
    smsRongyidaSignName: payload.smsRongyidaSignName,
    smsEnabled: payload.smsEnabled,
    loginSmsOpen: payload.loginSmsOpen,
    integrityCheckOpen: payload.integrityCheckOpen,
    smsWsbPhone: payload.smsWsbPhone,
    smsReturnLinkUrl: payload.smsReturnLinkUrl,
    smsCaseTimeoutDays: payload.smsCaseTimeoutDays,
    vcsEnabled: payload.vcsEnabled,
    vcsBaseUrl: payload.vcsBaseUrl,
    vcsGroupId: payload.vcsGroupId,
    vcsUsername: payload.vcsUsername,
    ...(payload.vcsPassword?.trim() ? { vcsPassword: payload.vcsPassword.trim() } : {}),
    vcsConnectTimeoutMs: payload.vcsConnectTimeoutMs,
    vcsReadTimeoutMs: payload.vcsReadTimeoutMs,
  })
  return { logoUrl: systemLogo, customerServiceGroupQrUrl }
}

/** 上传 LOGO 到文件服务，返回可访问 URL */
export async function uploadSystemLogo(file: File): Promise<string> {
  const fd = new FormData()
  fd.append('file', file)
  const url = await http.upload<string>('/dev/file/uploadDynamicReturnUrl', fd)
  if (typeof url === 'string' && url.trim()) return url.trim()
  throw new Error('上传成功但未返回图片地址')
}

/** 导出面板配置 JSON */
export async function exportSystemConfigFile(): Promise<void> {
  await download(`${PANEL_PREFIX}/export`, 'yj-system-config-panel.json')
}

/** 下载系统操作手册：优先走带登录态的 /dev/file/download?id=（与附件一致） */
export async function downloadOperationManual(): Promise<void> {
  const meta = await fetchOperationManualMeta()
  const name = meta.operationManualFileName?.trim() || '系统操作手册.pdf'
  const fileId = meta.operationManualFileId?.trim()
  if (fileId) {
    await download(`/dev/file/download?id=${encodeURIComponent(fileId)}`, name, { timeout: 120000 })
    return
  }
  const url = meta.operationManualUrl?.trim()
  if (!url) {
    throw new Error('尚未上传系统操作手册')
  }
  if (url.includes('/dev/file/download')) {
    const path = url.startsWith('http') ? `${new URL(url).pathname}${new URL(url).search}` : url
    await download(path, name, { timeout: 120000 })
    return
  }
  openManualPublicUrl(url, name)
}

export interface OperationManualMeta {
  operationManualFileName?: string
  operationManualFileId?: string
  operationManualUrl?: string
  operationManualUpdatedAt?: string
}

export async function fetchOperationManualMeta(): Promise<OperationManualMeta> {
  const data = await http.get<OperationManualMeta>(`${PANEL_PREFIX}/operationManual/meta`)
  return {
    operationManualFileName: String(data?.operationManualFileName ?? '').trim(),
    operationManualFileId: String(data?.operationManualFileId ?? '').trim(),
    operationManualUrl: String(data?.operationManualUrl ?? '').trim(),
    operationManualUpdatedAt: String(data?.operationManualUpdatedAt ?? '').trim(),
  }
}

function openManualPublicUrl(url: string, filename?: string) {
  const href = url.trim()
  const link = document.createElement('a')
  link.href = href
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  if (filename?.trim()) {
    link.download = filename.trim()
  }
  document.body.appendChild(link)
  link.click()
  link.remove()
}

/** 上传系统操作手册（MinIO 返回公网 URL，与系统 LOGO 同一套引擎） */
export async function uploadOperationManual(file: File): Promise<OperationManualMeta> {
  const fd = new FormData()
  fd.append('file', file)
  const data = await http.upload<OperationManualMeta>(`${PANEL_PREFIX}/operationManual`, fd, {
    timeout: 120000,
  })
  return {
    operationManualFileName: String(data?.operationManualFileName ?? file.name).trim(),
    operationManualFileId: String(data?.operationManualFileId ?? '').trim(),
    operationManualUrl: String(data?.operationManualUrl ?? '').trim(),
    operationManualUpdatedAt: String(data?.operationManualUpdatedAt ?? '').trim(),
  }
}

export interface IcspKeyItem {
  ready: boolean
  keyUsages: '0' | '1'
  keyCodeMasked: string
  periodDays: number | null
  expirationTime: string
  createdAt: string
}

export interface IcspKeyStatus {
  icspEnabled: boolean
  confidentiality: IcspKeyItem
  integrity: IcspKeyItem
}

function mapIcspKeyItem(
  raw: Partial<IcspKeyItem> | null | undefined,
  fallback: '0' | '1',
): IcspKeyItem {
  const usages = String(raw?.keyUsages ?? fallback) === '1' ? '1' : '0'
  const period = Number(raw?.periodDays)
  return {
    ready: raw?.ready === true,
    keyUsages: usages,
    keyCodeMasked: String(raw?.keyCodeMasked ?? '').trim(),
    periodDays: Number.isFinite(period) && period >= 1 ? period : null,
    expirationTime: String(raw?.expirationTime ?? '').trim(),
    createdAt: String(raw?.createdAt ?? '').trim(),
  }
}

function mapIcspKeyStatus(raw?: Partial<IcspKeyStatus> | null): IcspKeyStatus {
  return {
    icspEnabled: raw?.icspEnabled === true,
    confidentiality: mapIcspKeyItem(raw?.confidentiality, '0'),
    integrity: mapIcspKeyItem(raw?.integrity, '1'),
  }
}

export async function fetchIcspKeyStatus(): Promise<IcspKeyStatus> {
  const raw = await http.get<Partial<IcspKeyStatus>>(`${PANEL_PREFIX}/icspKeys`)
  return mapIcspKeyStatus(raw)
}

/** 编辑单把密钥；period 为空则 createKey 不传 period */
export async function saveIcspKey(
  keyUsages: '0' | '1',
  period?: number | null,
): Promise<IcspKeyStatus> {
  const body: { keyUsages: string; period?: number } = { keyUsages }
  if (period != null && period >= 1) body.period = period
  const raw = await http.post<Partial<IcspKeyStatus>>(`${PANEL_PREFIX}/icspKeys`, body)
  return mapIcspKeyStatus(raw)
}

/** 导出机密性/完整性密钥 JSON（含 keyCode） */
export async function exportIcspKeysFile(): Promise<void> {
  await download(`${PANEL_PREFIX}/icspKeys/export`, 'yj-icsp-keys.json')
}

/** 从 JSON 导入密钥，不调用 createKey */
export async function importIcspKeysFile(file: File): Promise<IcspKeyStatus> {
  const fd = new FormData()
  fd.append('file', file)
  const raw = await http.upload<Partial<IcspKeyStatus>>(`${PANEL_PREFIX}/icspKeys/import`, fd)
  return mapIcspKeyStatus(raw)
}
